/**
 * Step 3 - Defaults & Opções (workspace/pipeline/fase/origem + vendedor + tags + duplicados).
 *
 * CHANGELOG:
 *   RELEASE: 2026-05-16 v1.2.0 -
 *     + Bloco "Vendedor" com 3 modos (Manual | Fila de distribuição |
 *       Vindo do arquivo), reaproveitando o padrão e os componentes do
 *       CrmIntegrationSection (SellerSelector + Select de filas).
 *     + Toggle "Criar tags automaticamente se não existirem".
 *     + Sem alterações no fluxo de workspace/pipeline/fase/origem.
 *   RELEASE: 2026-05-16 v1.1.0 - Workspace obrigatório + origem por LEAD_SOURCES.
 *   RELEASE: 2026-05-16 v1.0.0 - Versão inicial.
 */

import { useQuery } from '@tanstack/react-query';
import { Info, Tag, UserSquare2 } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { SellerSelector } from '@/components/crm/SellerSelector';
import { useLeadSources } from '@/hooks/useLeadSources';
import type {
  Pipeline,
  Phase,
  Workspace,
  SellerMode,
  DistributionQueueOption,
} from './types';

const logger = createLogger('Step3Defaults');

interface Step3DefaultsProps {
  workspaces: Workspace[];
  pipelines: Pipeline[];
  phases: Phase[];
  defaultWorkspace: string;
  defaultPipeline: string;
  defaultPhase: string;
  defaultSource: string;
  silentMode: boolean;
  duplicateBehavior: 'skip' | 'create_new';
  // v1.2.0 - vendedor
  sellerMode: SellerMode;
  defaultSellerId: string | null;
  defaultQueueId: string | null;
  // v1.2.0 - tags
  autoCreateTags: boolean;
  onDefaultWorkspaceChange: (v: string) => void;
  onDefaultPipelineChange: (v: string) => void;
  onDefaultPhaseChange: (v: string) => void;
  onDefaultSourceChange: (v: string) => void;
  onSilentModeChange: (v: boolean) => void;
  onDuplicateBehaviorChange: (v: 'skip' | 'create_new') => void;
  onSellerModeChange: (v: SellerMode) => void;
  onDefaultSellerIdChange: (v: string | null) => void;
  onDefaultQueueIdChange: (v: string | null) => void;
  onAutoCreateTagsChange: (v: boolean) => void;
}

export function Step3Defaults(props: Step3DefaultsProps) {
  const {
    workspaces, pipelines, phases,
    defaultWorkspace, defaultPipeline, defaultPhase, defaultSource,
    silentMode, duplicateBehavior,
    sellerMode, defaultSellerId, defaultQueueId,
    autoCreateTags,
    onDefaultWorkspaceChange, onDefaultPipelineChange, onDefaultPhaseChange,
    onDefaultSourceChange, onSilentModeChange, onDuplicateBehaviorChange,
    onSellerModeChange, onDefaultSellerIdChange, onDefaultQueueIdChange,
    onAutoCreateTagsChange,
  } = props;

  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  // Catálogo dinâmico de origens (sistema + custom do tenant)
  const { activeSources: leadSources } = useLeadSources();

  const pipelineDisabled = !defaultWorkspace;
  const phaseDisabled = !defaultPipeline;

  // Filas de distribuição (mesma query do CrmIntegrationSection: context_type crm|all).
  const { data: queues = [], isLoading: loadingQueues } = useQuery({
    queryKey: ['distribution-queues-import-deals', tenantId],
    queryFn: async (): Promise<DistributionQueueOption[]> => {
      if (!tenantId) return [];
      const { data, error } = await supabase
        .from('distribution_queues')
        .select('id, name, distribution_method, context_type')
        .eq('tenant_id', tenantId)
        .eq('is_active', true)
        .in('context_type', ['crm', 'all'])
        .order('name');
      if (error) {
        logger.error('Erro ao buscar filas de distribuição', error);
        return [];
      }
      return (data || []).map((q) => ({
        id: q.id,
        name: q.name,
        distribution_method: q.distribution_method,
      }));
    },
    enabled: !!tenantId && sellerMode === 'queue',
    staleTime: 60_000,
  });

  return (
    <div className="space-y-4">
      <div>
        <Label>Workspace *</Label>
        <Select value={defaultWorkspace} onValueChange={onDefaultWorkspaceChange}>
          <SelectTrigger>
            <SelectValue placeholder={workspaces.length ? 'Selecione o workspace' : 'Carregando workspaces...'} />
          </SelectTrigger>
          <SelectContent>
            {workspaces.map((w) => (
              <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground mt-1">
          Os negócios serão criados no workspace selecionado. Apenas pipelines deste workspace aparecerão abaixo.
        </p>
      </div>

      <div>
        <Label>Pipeline padrão *</Label>
        <Select value={defaultPipeline} onValueChange={onDefaultPipelineChange} disabled={pipelineDisabled}>
          <SelectTrigger>
            <SelectValue placeholder={pipelineDisabled ? 'Escolha um workspace primeiro' : 'Selecione'} />
          </SelectTrigger>
          <SelectContent>
            {pipelines.map((p) => (
              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>Fase padrão *</Label>
        <Select value={defaultPhase} onValueChange={onDefaultPhaseChange} disabled={phaseDisabled}>
          <SelectTrigger>
            <SelectValue placeholder={phaseDisabled ? 'Escolha um pipeline primeiro' : 'Selecione'} />
          </SelectTrigger>
          <SelectContent>
            {phases.map((p) => (
              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>Origem padrão</Label>
        <Select value={defaultSource} onValueChange={onDefaultSourceChange}>
          <SelectTrigger><SelectValue placeholder="Selecione a origem" /></SelectTrigger>
          <SelectContent>
            {leadSources.map((s) => (
              <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground mt-1">
          Aplicada às linhas sem coluna "origem" mapeada. Valores divergentes da lista oficial são rejeitados.
        </p>
      </div>

      {/* =====================================================================
          v1.2.0 - Atribuição de vendedor (Manual | Fila | Vindo do arquivo)
          Reaproveita o mesmo padrão visual e SellerSelector usado em
          CrmIntegrationSection (seção "Atribuição do vendedor").
         ===================================================================== */}
      <div className="rounded-lg border p-4 space-y-3">
        <div className="flex items-center gap-2">
          <UserSquare2 className="h-4 w-4 text-primary" />
          <Label className="text-base">Atribuição do vendedor</Label>
        </div>

        <Select
          value={sellerMode}
          onValueChange={(v) => onSellerModeChange(v as SellerMode)}
        >
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="manual">Manual (vendedor padrão para todos)</SelectItem>
            <SelectItem value="queue">Fila de distribuição</SelectItem>
            <SelectItem value="from_file">Vindo do arquivo (coluna com e-mail)</SelectItem>
          </SelectContent>
        </Select>

        {sellerMode === 'manual' && (
          <div className="space-y-1">
            <Label>Vendedor padrão <span className="text-destructive">*</span></Label>
            <SellerSelector
              value={defaultSellerId}
              onSelect={onDefaultSellerIdChange}
              placeholder="Selecione o vendedor"
            />
            <p className="text-xs text-muted-foreground">
              Todos os negócios importados serão atribuídos a este vendedor.
            </p>
          </div>
        )}

        {sellerMode === 'queue' && (
          <div className="space-y-1">
            <Label>Fila de distribuição <span className="text-destructive">*</span></Label>
            <Select
              value={defaultQueueId || ''}
              onValueChange={(v) => onDefaultQueueIdChange(v || null)}
              disabled={loadingQueues}
            >
              <SelectTrigger>
                <SelectValue placeholder={loadingQueues ? 'Carregando filas...' : 'Selecione a fila'} />
              </SelectTrigger>
              <SelectContent>
                {queues.map((q) => (
                  <SelectItem key={q.id} value={q.id}>
                    {q.name} ({q.distribution_method})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              O sistema distribui automaticamente cada negócio para um vendedor da fila.
            </p>
          </div>
        )}

        {sellerMode === 'from_file' && (
          <div className="flex gap-2 rounded-md border border-blue-300/60 bg-blue-50 p-3 text-blue-900 dark:border-blue-400/30 dark:bg-blue-950/40 dark:text-blue-100">
            <Info className="h-4 w-4 shrink-0 mt-0.5" />
            <div className="text-xs space-y-1">
              <p className="font-medium">Coluna "Vendedor (e-mail)" obrigatória.</p>
              <p>
                Volte para o passo de mapeamento e aponte a coluna do CSV que
                contém o e-mail do vendedor. Linhas com e-mails que não
                correspondam a vendedores cadastrados serão marcadas como
                inválidas (sem fallback).
              </p>
            </div>
          </div>
        )}
      </div>

      {/* =====================================================================
          v1.2.0 - Toggle: criar tags automaticamente
         ===================================================================== */}
      <div className="flex items-center justify-between rounded-lg border p-4">
        <div className="flex items-start gap-3">
          <Tag className="h-4 w-4 text-primary mt-0.5" />
          <div>
            <Label className="text-base">Criar tags automaticamente</Label>
            <p className="text-xs text-muted-foreground mt-1">
              Quando ligado, tags presentes no CSV que ainda não existem no
              sistema serão criadas. Quando desligado, apenas tags já
              cadastradas serão vinculadas.
            </p>
          </div>
        </div>
        <Switch checked={autoCreateTags} onCheckedChange={onAutoCreateTagsChange} />
      </div>

      <div>
        <Label>Comportamento de duplicados</Label>
        <Select value={duplicateBehavior} onValueChange={(v) => onDuplicateBehaviorChange(v as 'skip' | 'create_new')}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="skip">Pular duplicados (recomendado)</SelectItem>
            <SelectItem value="create_new">Criar mesmo se duplicado</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center justify-between rounded-lg border p-4">
        <div>
          <Label className="text-base">Modo silencioso</Label>
          <p className="text-xs text-muted-foreground mt-1">
            Suprime notificações, automações e cadências durante a importação.
            Recomendado para volumes grandes.
          </p>
        </div>
        <Switch checked={silentMode} onCheckedChange={onSilentModeChange} />
      </div>
    </div>
  );
}
